import {
  alpha,
  BrandPalette,
  createTheme as createMuiTheme,
  ThemeOptions,
} from '@mui/material'
import { deepmerge } from '@mui/utils'

import { Icon } from '../icon'

const firstItemIcon = () => <Icon icon="Pagination" rotation="180" size="sm" />
const lastItemIcon = () => <Icon icon="Pagination" size="sm" />
const prevItemIcon = () => <Icon icon="Chevron" rotation="180" size="sm" />
const nextItemIcon = () => <Icon icon="Chevron" size="sm" />

declare module '@mui/material/styles' {
  interface BrandPalette {
    green: { main: string; contrastText: string }
    cheviot: {
      main: string
      light: string
      xLight: string
      contrastText: string
      secondary: string
    }
    grey: {
      dark: string
      main: string
      light: string
      xLight: string
      primary2: string
      primary3: string
      primary4: string
      primary5: string
      primary6: string
      contrastText: string
      faint: string
    }
    negative: {
      main: string
    }
    orange: {
      secondary: string
    }
    action: {
      pending: string
      error: string
      success: string
    }
  }

  interface Palette {
    brand: BrandPalette
  }

  interface PaletteOptions {
    brand: BrandPalette
  }

  interface TypographyVariants {
    body3: React.CSSProperties
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    body3?: React.CSSProperties
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    body3: true
  }
}

type ThemeOptionParams = {
  font: { body: string; heading: string }
  overrides?: Partial<ThemeOptions>
}

export function createThemeOptions(opts?: ThemeOptionParams) {
  const { body, heading } = opts?.font ?? {}

  const primary = {
    main: '#023843',
  }

  const error = {
    main: '#9F1D16',
    contrastText: 'white',
    light: '#FFE6F3',
    light2: '#DF1F11',
  }

  const success = {
    main: '#085D30',
    light: '#DCFAE0',
  }

  const brandOrange = {
    secondary: '#D68063',
  }

  const brandGreen = {
    primary: primary.main,
    main: success.main,
    contrastText: 'white',
  }

  const brand: BrandPalette = {
    // Custom colors not found on the default mui theme
    green: brandGreen,
    cheviot: {
      main: '#007FB0',
      secondary: '#1E6170',
      light: '#AEBDC9',
      xLight: '#D8F0EE',
      contrastText: primary.main,
    },
    grey: {
      dark: '#6F6F6F',
      main: '#e0e0e0',
      light: '#F4F4F4',
      xLight: '#FAFAFA',
      primary2: '#DFE5E9',
      primary3: '#D9D9D9',
      primary4: '#C1C1C1',
      primary5: '#E7E5E5',
      primary6: '#7D7D7D',
      contrastText: primary.main,
      faint: '#f8f8f8',
    },
    negative: {
      main: error.main,
    },
    orange: brandOrange,
    action: {
      pending: brandOrange.secondary,
      error: error.main,
      success: brandGreen.main,
    },
  }

  const headingStyle = {
    fontFamily: heading,
    fontWeight: 500,
    color: primary.main,
  }

  const typography = {
    fontSize: 16,
    fontFamily: body,
    h1: headingStyle,
    h2: { ...headingStyle, fontSize: '38px', fontWeight: 300 },
    h3: { ...headingStyle, fontSize: '28px' },
    h4: { ...headingStyle, fontSize: '22px' },
    h5: { ...headingStyle, fontSize: '18px' },
    h6: headingStyle,
    body1: {
      fontSize: '16px',
    },
    body2: {
      fontSize: '14px',
      fontWeight: 600,
      lineHeight: '20px',
      letterSpacing: '0.32px',
    },
    body3: {
      fontSize: '12px',
    },
  }

  const themeOptions: ThemeOptions = {
    palette: {
      primary: primary,
      secondary: {
        main: '#BAA94A',
        dark: '#ab9412',
      },
      brand,
      text: {
        primary: '#333333',
        secondary: '#3E5060',
      },
      success,
      warning: {
        main: '#DEA500',
      },
      error,
    },
    shape: {
      borderRadius: 8,
    },
    breakpoints: {
      values: {
        xs: 0, // mobile
        sm: 600, // mobile/tablet
        md: 1025, // tablet
        lg: 1200, // tablet/desktop
        xl: 1536, // desktop
      },
    },
    typography,
    components: {
      MuiTabs: {
        defaultProps: {
          variant: 'scrollable',
          scrollButtons: true,
          allowScrollButtonsMobile: true,
          slotProps: {
            list: {
              style: {
                margin: 0,
                padding: 0,
              },
            },
            scrollButtons: {
              style: {
                pointerEvents: 'none',
                position: 'absolute',
                height: '100%',
                top: 0,
                width: 32,
                display: 'block',
                zIndex: 1,
              },
            },
            indicator: {
              sx: {
                zIndex: 2,
                height: 6,
                backgroundColor: `var(--mui-palette-secondary-main)`,
              },
            },
          },
        },
        styleOverrides: {
          root: {
            '.MuiTabScrollButton-horizontal:first-of-type': {
              left: 0,
              background:
                'linear-gradient(to right, var(--mui-palette-primary-dark), transparent)',
            },
            '.MuiTabScrollButton-horizontal:last-of-type': {
              right: 0,
              background:
                'linear-gradient(to right, transparent, var(--mui-palette-primary-dark))',
            },
            '.MuiTabScrollButton-horizontal .MuiSvgIcon-root': {
              display: 'none',
            },
          },
        },
      },
      MuiTab: {
        defaultProps: {
          sx: {
            color: 'inherit',
            textTransform: 'none',
            whiteSpace: 'nowrap',
            backgroundImage: 'none !important',
            backgroundColor: 'transparent',
            transition: 'background-color 0.3s ease-in-out !important',
          },
        },
        styleOverrides: {
          root: {
            '&.Mui-selected': {
              fontWeight: 'bold',
              color: 'white',
            },
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          variant: 'outlined',
        },
        styleOverrides: {},
      },
      MuiCheckbox: {
        styleOverrides: {
          root: ({ theme }) => ({
            color: theme.palette.primary.main,
            backgroundColor: 'transparent',
            padding: 1,
            borderRadius: 4,
            marginRight: 8,

            '&:hover, &:focus': {
              backgroundColor: theme.palette.brand.cheviot.xLight,
            },

            '&.Mui-disabled': {
              color: theme.palette.brand.grey.primary6,
            },
          }),
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            marginTop: -5,
          },
          shrink: {
            marginTop: 0,
          },
        },
      },
      MuiOutlinedInput: {
        defaultProps: {
          autoComplete: 'off',
        },
        styleOverrides: {
          root: {
            padding: 0,
            borderRadius: 4,
          },
          input: {
            padding: '10px 12px',
            height: 'auto',
            '&:-webkit-autofill': {
              WebkitBoxShadow: '0 0 0 100px var(--primary-weak) inset',
              WebkitTextFillColor: 'var(--text-primary)',
              CaretColor: 'var(--text-primary)',
            },
          },
          notchedOutline: {
            borderColor: primary.main,
          },
        },
      },
      MuiCard: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: ({ theme }) => ({
            border: `1px solid ${theme.palette.brand.grey.light}`,
          }),
        },
      },
      MuiInputBase: {
        styleOverrides: {
          input: ({ theme }) => ({
            '&::placeholder, &::-webkit-input-placeholder': {
              opacity: '1',
              color: theme.palette.brand.grey.dark,
            },
          }),
        },
      },
      MuiAlert: {
        defaultProps: {
          iconMapping: {
            success: <Icon icon="TickInCircle" size="lg" />,
            error: <Icon icon="Warning" size="lg" />,
            warning: <Icon icon="Information" size="lg" />,
            info: <Icon icon="Tool" size="lg" />,
          },
        },
        styleOverrides: {
          root: ({ theme, ownerState }) => ({
            padding: `${theme.spacing(1.5)} ${theme.spacing(2)}`,
            borderLeft: '5px solid',
            borderRadius: 4,
            borderLeftColor: {
              error: theme.palette[ownerState.severity!]?.main ?? 'transparent',
              info: theme.palette.primary.main ?? 'transparent',
              success:
                theme.palette[ownerState.severity!]?.main ?? 'transparent',
              warning: '#F39200',
            }[ownerState.severity!],
            boxShadow: theme.shadows[0],
            fontSize: theme.typography.body1.fontSize,
            fontWeight: theme.typography.fontWeightRegular,
            rowGap: 1,
            alignItems: 'center',
            background: {
              error: theme.palette.error.light,
              info: alpha(theme.palette.primary.main, 0.2),
              success: theme.palette.success.light,
              warning: '#FFF8D6',
            }[ownerState.severity!],
            '.MuiAlert-message': {
              color: theme.palette.text.primary,
            },
            '.MuiAlert-icon': {
              alignItems: 'center',
              color: {
                error: undefined,
                info: theme.palette.primary.main,
                success: undefined,
                warning: '#F39200',
              }[ownerState.severity!],
            },
            ' .MuiAlert-action': {
              padding: 0,
              marginRight: 0,
              alignSelf: 'center',
              marginLeft: 'auto',
            },
            '.MuiSnackbar-root &': {
              padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
            },
            [theme.breakpoints.down('sm')]: {
              flexWrap: 'wrap',
              ' .MuiAlert-action': {
                marginLeft: 0,
              },
            },
          }),
          filled: ({ theme, ownerState }) => ({
            borderRadius: 4,
            borderLeft: 'none',
            boxShadow: theme.shadows[8],
            background: {
              info: 'white',
              success: success.light,
              error: error.light,
              warning: 'white',
            }[ownerState.severity!],
            color: {
              info: theme.palette.text.primary,
              success: theme.palette.text.primary,
              error: theme.palette.text.primary,
              warning: theme.palette.text.primary,
            }[ownerState.severity!],
            '.MuiAlert-icon': {
              alignItems: 'center',
              color: {
                info: success.main,
                success: success.main,
                error: error.main,
                warning: error.main,
              }[ownerState.severity!],
            },
            ' .MuiAlert-action': {
              marginLeft: theme.spacing(2),
              alignSelf: 'center',
            },
          }),
        },
      },
      MuiPagination: {
        styleOverrides: {
          root: ({ theme }) => ({
            color: theme.palette.brand.grey.primary2,
          }),
        },
      },
      MuiPaginationItem: {
        defaultProps: {
          components: {
            first: firstItemIcon,
            last: lastItemIcon,
            next: nextItemIcon,
            previous: prevItemIcon,
          },
        },
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: Number(theme.shape.borderRadius) / 2,
            '&:hover:not(.Mui-selected)': {
              backgroundColor: 'transparent',
              outline: `1px solid ${theme.palette.brand.grey.dark}`,
              outlineOffset: '-1px',
            },
            '&:focus:not(.Mui-selected)': {
              backgroundColor: 'transparent',
              outline: `1px solid ${theme.palette.brand.grey.dark}`,
              outlineOffset: '-1px',
            },
          }),
        },
      },
      MuiButton: {
        styleOverrides: {
          root: ({ theme, ownerState }) => ({
            boxShadow: 'none',
            '&:hover': {
              boxShadow: {
                contained: `0px 1px 10px 0px rgba(0, 0, 0, 0.12), 0px 4px 5px 0px rgba(0, 0, 0, 0.14), 0px 2px 4px -1px rgba(0, 0, 0, 0.20)`,
                outlined: `0px 2px 4px -2px rgba(0, 0, 0, 0.50), 0px 4px 6px -1px rgba(0, 0, 0, 0.10)`,
                text: 'none',
              }[ownerState.variant!],
              backgroundColor: {
                contained:
                  ownerState.color === 'secondary'
                    ? theme.palette.secondary.main
                    : theme.palette.primary.main,
                outlined: `#00283a19`,
                text: 'transparent',
              }[ownerState.variant!],
              textDecoration: {
                contained: 'none',
                outlined: 'none',
                text: 'underline',
              }[ownerState.variant!],
            },
            '&:focus': {
              boxShadow: {
                contained: `0px 1px 10px 0px rgba(0, 0, 0, 0.12), 0px 4px 5px 0px rgba(0, 0, 0, 0.14), 0px 2px 4px -1px rgba(0, 0, 0, 0.20)`,
                outlined: `0px 2px 4px -2px rgba(0, 0, 0, 0.50), 0px 4px 6px -1px rgba(0, 0, 0, 0.10)`,
                text: 'none',
              }[ownerState.variant!],
            },
            '&:active': {
              backgroundColor: {
                // extra hex units contol the opacity
                contained: `${primary}33`,
                outlined: `${primary}33`,
                text: `${primary}33`,
              }[ownerState.variant!],
            },
          }),
        },
      },
      MuiRadio: {
        defaultProps: {
          size: 'small',
        },
        styleOverrides: {
          root: ({ theme }) => ({
            padding: 0,
            color: theme.palette.brand.grey.dark,
            '&:hover, &:focus': {
              color: theme.palette.secondary.main,
            },
            '&.Mui-disabled.Mui-checked': {
              color: 'white',
            },
          }),
        },
      },
      MuiToggleButtonGroup: {
        styleOverrides: {
          root: ({ theme }) => ({
            '& .MuiToggleButton-root': {
              textTransform: 'none',
              transition: '0.1s ease',
              backgroundColor: 'white',
              paddingTop: theme.spacing(1),
              paddingBottom: theme.spacing(1),
              color: theme.palette.brand.grey.dark,
              border: `1px solid ${theme.palette.brand.grey.primary4}`,

              '&.Mui-selected': {
                color: 'white',
                backgroundColor: theme.palette.primary.main,
                border: `1px solid ${theme.palette.primary.main}`,
                '&:hover, &:focus': {
                  color: 'white',
                  backgroundColor: theme.palette.primary.main,
                },
              },
              '&:hover': {
                color: 'white',
                borderColor: theme.palette.primary.main,
                backgroundColor: theme.palette.primary.main,
              },
              '&:focus': {
                color: 'white',
                borderColor: theme.palette.primary.main,
                backgroundColor: theme.palette.primary.main,
              },
              '&.Mui-disabled': {
                opacity: 0.6,
                color: theme.palette.brand.grey.dark,
                backgroundColor: theme.palette.brand.grey.light,
                border: `1px solid ${theme.palette.brand.grey.main}`,
              },
            },
          }),
        },
      },
    },
  }

  if (opts?.overrides === undefined) {
    return themeOptions
  }

  return deepmerge(themeOptions, opts.overrides)
}

export function createTheme(opts: ThemeOptionParams) {
  return createMuiTheme(createThemeOptions(opts))
}

export * from './theme-provider'
export * from './util'
